import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { RouterlessModal } from '@/se/components/Modal';
import type { DelayReasonModalProps } from '../../types/PatientScreenTypes';
import {
  OTHER_REASON_VALUE,
  delayReasonOptions,
  parseDelayReasonValue,
} from '@/components/entities/patient/delayReasons/delayReasons';
import { usePacuOptionalProcedureSteps, PacuStepNames } from '@/hooks/usePacuOptionalProcedureSteps';

/**
 * Delay reason picker modal
 * Allows selecting a reason for delays in patient flow
 */
export function PatientDelayReasonModal({ delayType, onSubmit, onCancel, currentReason }: DelayReasonModalProps) {
  const pacuStepNames = usePacuOptionalProcedureSteps().names;
  const prefill = parseDelayReasonValue(delayType, currentReason);
  const [selectedReason, setSelectedReason] = useState<string>(
    prefill.kind === 'option' ? prefill.option.value : prefill.kind === 'other' ? OTHER_REASON_VALUE : ''
  );
  const [customReason, setCustomReason] = useState<string>(prefill.kind === 'other' ? prefill.custom : '');

  const handleSubmit = useCallback(async () => {
    const reason = selectedReason === OTHER_REASON_VALUE ? customReason : selectedReason;
    if (reason) {
      await onSubmit(reason);
      onCancel(); // Close modal after submission
    }
  }, [selectedReason, customReason, onSubmit, onCancel]);

  // Shared, DB-backed option list for this milestone
  const reasons = delayReasonOptions[delayType];

  return (
    <RouterlessModal title={`${getDelayTitle(delayType, pacuStepNames)} - Delay Reason`} onClose={onCancel}>
      <Content>
        <ReasonsList>
          {reasons.map(reason => (
            <ReasonButton
              key={reason.value}
              selected={selectedReason === reason.value}
              onClick={() => setSelectedReason(reason.value)}
            >
              {reason.label}
            </ReasonButton>
          ))}
        </ReasonsList>

        {selectedReason === OTHER_REASON_VALUE && (
          <CustomReasonInput
            placeholder="Enter custom reason..."
            value={customReason}
            onChange={e => setCustomReason(e.target.value)}
            autoFocus
          />
        )}

        <SubmitButton
          disabled={!selectedReason || (selectedReason === OTHER_REASON_VALUE && !customReason)}
          onClick={handleSubmit}
        >
          Submit
        </SubmitButton>
      </Content>
    </RouterlessModal>
  );
}

function getDelayTitle(delayType: string, names: PacuStepNames): string {
  switch (delayType) {
    case 'pacuOut':
      return 'PACU Discharge';
    case 'oob':
      return names.transferredToChair;
    case 'pt':
      return names.transferredToPT;
    case 'ptComplete':
      return names.ptComplete;
    default:
      return 'Action';
  }
}

const Content = styled.div`
  padding: 0 1em 1em;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReasonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
`;

const ReasonButton = styled.button<{ selected: boolean }>`
  padding: 1rem;
  border: 2px solid ${props => (props.selected ? '#00b641' : 'rgba(255, 255, 255, 0.2)')};
  background: ${props => (props.selected ? 'rgba(0, 182, 65, 0.2)' : 'rgba(255, 255, 255, 0.05)')};
  color: #fff;
  font-size: 1rem;
  border-radius: 0.25rem;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;

  &:hover {
    background: ${props => (props.selected ? 'rgba(0, 182, 65, 0.3)' : 'rgba(255, 255, 255, 0.1)')};
  }
`;

const CustomReasonInput = styled.input`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 1rem;
  border-radius: 0.25rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #00b641;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  border: none;
  background: #00b641;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #00a038;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
